import { Pressable, View } from 'react-native';
import { Minus, Plus } from 'lucide-react-native';
import { colors, weekdayShort, type Lang, type Weekday } from '@hogar/shared';
import { Chip, Txt } from './ui';

const WEEK: Weekday[] = [0, 1, 2, 3, 4, 5, 6];

export function DaysPicker({ value, onChange, lang }: { value: Weekday[]; onChange: (d: Weekday[]) => void; lang: Lang }) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
      {WEEK.map((d) => {
        const on = value.includes(d);
        return (
          <Chip
            key={d}
            label={weekdayShort(d, lang)}
            on={on}
            onPress={() => onChange(on ? value.filter((x) => x !== d) : [...value, d].sort((a, b) => a - b))}
          />
        );
      })}
    </View>
  );
}

export function RoundButton({ Icon, onPress, label }: { Icon: typeof Plus; onPress: () => void; label: string }) {
  return (
    <Pressable
      accessibilityLabel={label}
      onPress={onPress}
      hitSlop={8}
      style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.primarySoft, alignItems: 'center', justifyContent: 'center' }}
    >
      <Icon size={20} color={colors.primary} />
    </Pressable>
  );
}

/** A number with minus and plus buttons. */
export function Stepper({ value, onChange, min, max, step = 1, format }: { value: number; onChange: (n: number) => void; min: number; max: number; step?: number; format?: (n: number) => string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
      <RoundButton Icon={Minus} label="-" onPress={() => onChange(Math.max(min, value - step))} />
      <Txt weight="semibold" size={20} style={{ minWidth: 70, textAlign: 'center' }}>
        {format ? format(value) : value}
      </Txt>
      <RoundButton Icon={Plus} label="+" onPress={() => onChange(Math.min(max, value + step))} />
    </View>
  );
}

/** "09:00" in 30-minute steps. */
export function TimeStepper({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [h, m] = value.split(':').map(Number);
  const minutes = h * 60 + m;
  const fmt = (n: number) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
  return <Stepper value={minutes} min={5 * 60} max={22 * 60} step={30} format={fmt} onChange={(n) => onChange(fmt(n))} />;
}
